import { useState } from 'react';
import { ApiError } from '../../../lib/api';
import {
  operatorConsole,
  type PoolShippingDetail,
  type SandboxActionName,
  type SandboxShipParams,
  type ShippingSandbox,
} from '../../../lib/api/operatorConsole';
import { SANDBOX_ACTIONS } from './sandboxActions';
import { SandboxActionRow } from './SandboxActionRow';
import { SandboxOutcome } from './SandboxOutcome';
import { ShipForm } from './ShipForm';

interface Props {
  shipping: PoolShippingDetail;
  sandbox: ShippingSandbox;
  /** Another write on this delivery is in flight (a card being reported). */
  disabled: boolean;
  /** Lets the page hold Can't ship while an action here is in flight. */
  onBusyChange: (busy: boolean) => void;
  onUpdated: (shipping: PoolShippingDetail) => void;
}

// Staging-only test controls. Rendered only when the backend sends
// `sandbox`, which it never does in production.
export function SandboxPanel({ shipping, sandbox, disabled, onBusyChange, onUpdated }: Props) {
  const [running, setRunning] = useState<SandboxActionName | null>(null);
  const [shipFormOpen, setShipFormOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const busy = disabled || running !== null;

  async function run(action: SandboxActionName, request: () => Promise<PoolShippingDetail>) {
    if (!window.confirm(SANDBOX_ACTIONS[action].confirm(shipping.id))) return;

    setProblem(null);
    setDone(null);
    setRunning(action);
    onBusyChange(true);
    try {
      onUpdated(await request());
      setDone(SANDBOX_ACTIONS[action].done);
      setShipFormOpen(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setProblem(err.message ?? 'The delivery did not accept this action.');
        await resync();
      } else {
        setProblem('Could not reach the registry. Try again.');
      }
    } finally {
      setRunning(null);
      onBusyChange(false);
    }
  }

  // A refusal usually means the delivery moved on without this page; reading
  // it again puts the buttons back in line with it. Awaited inside the busy
  // window, so a slow read cannot land after the next action.
  async function resync() {
    try {
      onUpdated(await operatorConsole.getShipping(shipping.id));
    } catch {
      // The refusal is already on screen; a failed re-read changes nothing.
    }
  }

  function rowProps(action: SandboxActionName) {
    const { label, runningLabel, hint } = SANDBOX_ACTIONS[action];
    return { label, runningLabel, hint, control: sandbox[action], running: running === action, busy };
  }

  return (
    <section
      aria-label="Staging sandbox"
      className="rounded-lg border border-dashed border-[var(--co-warning)] bg-ui-card p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-ui-text">Staging sandbox</h2>
        <span className="rounded px-2 py-0.5 text-[11px] font-medium text-[var(--co-warning)] ring-1 ring-[var(--co-warning)]">
          Staging only
        </span>
      </div>
      <p className="mt-1 text-[12px] text-ui-text-secondary">
        Move this test delivery yourself to receive the real webhooks and callbacks. To test a
        card that cannot ship, use Can&apos;t ship on that card first. These controls never
        appear in production.
      </p>

      <SandboxOutcome problem={problem} done={done} />

      <ul className="mt-2 divide-y divide-ui-border">
        <SandboxActionRow
          {...rowProps('mark_shipped')}
          onClick={() => setShipFormOpen((open) => !open)}
        />
        {shipFormOpen && sandbox.mark_shipped.available && (
          <ShipForm
            busy={busy}
            onClose={() => setShipFormOpen(false)}
            onSubmit={(params: SandboxShipParams) =>
              void run('mark_shipped', () =>
                operatorConsole.sandboxShipShipping(shipping.id, params),
              )
            }
          />
        )}
        <SandboxActionRow
          {...rowProps('mark_delivered')}
          onClick={() =>
            void run('mark_delivered', () => operatorConsole.sandboxDeliverShipping(shipping.id))
          }
        />
        <SandboxActionRow
          {...rowProps('cancel')}
          onClick={() =>
            void run('cancel', () => operatorConsole.sandboxCancelShipping(shipping.id))
          }
        />
      </ul>
    </section>
  );
}
